import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import ManagementModal from "../../../components/ui/modals/ManagementModal";


import {
  showErrorToast,
  showSuccessToast,
} from "../../../lib/toast";
import { getStakeholders } from "../../stakeholder/logic/stakeholderSlice";
import { updateStakeholder } from "../../stakeholder/logic/stakeholderThunks";
import { isCreditAreaOfFraud } from "../../caseinfo/logic/Slice";
import StakeholderSelectionList from "../../stakeholder/components/StakeholderSelectionList";
import { AddButton } from "../../stakeholder/components/AddStakeholderButton";
import PartyModal from "./PartyModal";
import AccountPartyModal from "./AccountPartyModal";

interface AddExistingStakeholderAsPartyProps {
  onClose: () => void;
  onBack?: () => void;
}

const AddExistingStakeholderAsParty: React.FC<
  AddExistingStakeholderAsPartyProps
> = ({ onClose, onBack }) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const isCredit = useAppSelector(isCreditAreaOfFraud);
  const stakeholders = useAppSelector(getStakeholders);

  const [selectedStakeholderIds, setSelectedStakeholderIds] = useState<
    string[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPartyModal, setShowPartyModal] = useState(false);

  const existingPartyIds = useMemo(
    () =>
      stakeholders
        .filter((stakeholder) => stakeholder.is_potential_perpetrator)
        .map((stakeholder) => stakeholder.stakeholder_id),
    [stakeholders]
  );

  const handleSubmit = async () => {
    if (!caseId || selectedStakeholderIds.length === 0) {
      return;
    }

    setIsLoading(true);

    try {
      await dispatch(
        updateStakeholder({
          caseId,
          stakeholderId: selectedStakeholderIds[0],
          request: {
            is_potential_perpetrator: true,
          },
        })
      ).unwrap();

      showSuccessToast("Party added successfully.");
      onClose();
    } catch {
      showErrorToast("Failed to add party. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectionClose = () => {
    if (isLoading) {
      return;
    }

    setSelectedStakeholderIds([]);
    setShowPartyModal(false);
    onClose();
  };

  const handleSelectionBack = onBack ?? handleSelectionClose;

  const handleAddNewParty = () => {
    setShowPartyModal(true);
  };

  const handlePartyClose = () => {
    setShowPartyModal(false);
    onClose();
  };

  const handlePartyBack = () => {
    setShowPartyModal(false);
  };

  return (
    <>
      {!showPartyModal && (
        <ManagementModal
          title="Add Party"
          onSubmit={handleSubmit}
          onBack={handleSelectionBack}
          onClose={handleSelectionClose}
          submitEnabled={
            selectedStakeholderIds.length > 0 && !isLoading
          }
          isLoading={isLoading}
        >
          <div className="flex w-full flex-col gap-3">
            <StakeholderSelectionList
              excludedStakeholderIds={existingPartyIds}
              onSelectionChange={setSelectedStakeholderIds}
            />

            <AddButton onClick={handleAddNewParty} />
          </div>
        </ManagementModal>
      )}

      {showPartyModal && (
        isCredit ? (
          <PartyModal
            onClose={handlePartyClose}
            onBack={handlePartyBack}
          />
        ) : (
          <AccountPartyModal
            onClose={handlePartyClose}
            onBack={handlePartyBack}
          />
        )
      )}
    </>
  );
};

export default AddExistingStakeholderAsParty;